import { Suspense, lazy, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router';
import { Header } from './Header';
import { BottomNav } from './BottomNav';
import { OfflineBanner } from './OfflineBanner';
import { PageContainer } from './PageContainer';
import { SessionExpiredModal } from '../auth/SessionExpiredModal';
import { useAuth } from '../../contexts/AuthContext';

// Lazy so the logged-out boot path doesn't pull LiveSessionBar's data hooks
// (and through them the aws-amplify SDK) into the entry chunk. It only renders
// for authenticated users anyway.
const LiveSessionBar = lazy(() =>
  import('./LiveSessionBar').then((m) => ({ default: m.LiveSessionBar }))
);

/**
 * Root layout component that wraps all pages.
 * Provides consistent header, navigation, and page container.
 * Includes session expired modal for graceful session handling.
 * 
 * Ref Mode gets a full-screen layout without header/footer for optimal
 * battle recording experience (Ref Mode spec Requirements 7.1-7.8).
 */
export function RootLayout() {
  const location = useLocation();
  const { isAuthenticated } = useAuth();

  // Ref mode needs full-screen without header/footer for optimal UX
  // Pattern: /tournament/:id/match/:matchId/ref
  const isRefMode = /\/tournament\/[^/]+\/match\/[^/]+\/ref/.test(location.pathname);

  // The public home scorecard (/play) is an immersive full-screen scoring
  // surface too — it owns the screen and provides its own exit control.
  const isPlayMode = location.pathname === '/play';

  // The app shell scrolls its own #app-scroll div, not the window, so
  // neither the browser's default nor React Router's <ScrollRestoration>
  // resets it on navigation — a link clicked partway down one page (e.g.
  // TwoPathsSection on the landing page) landed the user partway down the
  // *next* page too, since the div's scrollTop just carries over.
  useEffect(() => {
    document.getElementById('app-scroll')?.scrollTo(0, 0);
  }, [location.pathname]);

  // Full-screen mode - no header, no footer, no page container padding
  if (isRefMode || isPlayMode) {
    return (
      <div className="min-h-[100dvh] bg-bbx-dark-900">
        <Outlet />
        <SessionExpiredModal />
      </div>
    );
  }

  // App-shell layout: the content area is the only scroll region and the bottom
  // nav sits in flow beneath it, so page content can never render underneath the
  // nav (which previously caused taps near the bottom to hit nav items). REF-1.
  return (
    <div className="h-[100dvh] flex flex-col bg-bbx-dark-900 overflow-hidden">
      <div id="app-scroll" className="flex-1 overflow-y-auto">
        <Header />
        <OfflineBanner />
        {isAuthenticated && (
          <Suspense fallback={null}>
            <LiveSessionBar />
          </Suspense>
        )}
        <PageContainer>
          <Outlet />
        </PageContainer>
      </div>
      {isAuthenticated && <BottomNav />}
      <SessionExpiredModal />
    </div>
  );
}
